<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import {
    readCopyKeys,
    readProgressModel,
  } from '@/domain/menu-import/workspace-view';
  import type {
    IMenuImportPage,
    IMenuImportRead,
  } from '@/interfaces/IMenuImport';

  /**
   * The progress card of a running read (plan §4.4): the three stages
   * («Revisando las fotos», or «Revisando su carta» for typed or pasted text,
   * «Leyendo la página 2 de 3», «Ordenando categorías y precios»), one chip
   * per page, the dishes found so far, «Cancelar», the reassurance that the
   * screen can be closed (the email comes only when dishes were found) and,
   * after 90 s, that it is taking longer than usual. Screen readers hear the
   * count at most every 5 s. A store with dishes (an update) compares at the
   * end («Comparando con su menú») and is told nothing else changes.
   */
  const ANNOUNCE_MS = 5000;

  const props = defineProps<{
    read: IMenuImportRead;
    pages: IMenuImportPage[];
    slow: boolean;
    /** Dishes found so far in this read. */
    found: number;
    /** The store already has dishes: the read ends comparing with its menu. */
    update?: boolean;
  }>();
  const emit = defineEmits<{ (e: 'cancel'): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const trc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const model = computed(() => readProgressModel(props.read, props.pages));
  const copyKeys = computed(() =>
    readCopyKeys(props.read, props.pages, props.update === true)
  );
  const pageLine = computed(() =>
    tr('menuImport.reading.page', {
      page: model.value.current,
      total: model.value.total,
    })
  );
  const foundText = computed(() =>
    trc('menuImport.reading.found', props.found, { count: props.found })
  );

  //#region Throttled announcement
  const announced = ref(foundText.value);
  let lastAnnouncedAt = 0;
  let announceTimer: ReturnType<typeof setTimeout> | null = null;

  function announce() {
    announceTimer = null;
    lastAnnouncedAt = Date.now();
    announced.value = foundText.value;
  }

  watch(foundText, () => {
    if (announceTimer !== null) return;
    const wait = ANNOUNCE_MS - (Date.now() - lastAnnouncedAt);
    if (wait <= 0) announce();
    else announceTimer = setTimeout(announce, wait);
  });

  onBeforeUnmount(() => {
    if (announceTimer !== null) clearTimeout(announceTimer);
    announceTimer = null;
  });
  //#endregion
</script>

<template>
  <section class="v2-menu-read-progress" aria-live="polite">
    <ol class="stages">
      <li class="stage is-done">
        <span class="stage-mark" aria-hidden="true">
          <PosUiIcon name="check" :size="14" />
        </span>
        <span class="stage-text">{{ $t(copyKeys.checking) }}</span>
      </li>
      <li
        v-if="model.total > 0"
        class="stage"
        :class="model.organizing ? 'is-done' : 'is-now'"
      >
        <span class="stage-mark" aria-hidden="true">
          <PosUiIcon v-if="model.organizing" name="check" :size="14" />
        </span>
        <span class="stage-text">{{ pageLine }}</span>
      </li>
      <li class="stage" :class="model.organizing ? 'is-now' : 'is-pending'">
        <span class="stage-mark" aria-hidden="true" />
        <span class="stage-text">{{ $t(copyKeys.organizing) }}</span>
      </li>
    </ol>
    <p v-if="update" class="update-note">
      {{ $t('menuImport.update.readingNote') }}
    </p>
    <ul v-if="model.chips.length > 0" class="page-chips" aria-hidden="true">
      <li
        v-for="chip in model.chips"
        :key="chip.page"
        class="page-chip"
        :class="`is-${chip.state}`"
      >
        {{ chip.page }}
      </li>
    </ul>
    <div class="progress-foot">
      <p class="found" aria-hidden="true">{{ foundText }}</p>
      <button type="button" class="cancel-link" @click="emit('cancel')">
        {{ $t('menuImport.reading.cancel') }}
      </button>
    </div>
    <p class="announcer">{{ announced }}</p>
    <p v-if="slow" class="slow-line">{{ $t('menuImport.reading.slow') }}</p>
    <p class="leave-line">{{ $t('menuImport.reading.leaveOk') }}</p>
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-read-progress {
    @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4;
    .stages {
      @apply tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0;
    }
    .stage {
      @apply tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-neutral-500;
      .stage-mark {
        @apply tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-neutral-300;
      }
      &.is-done {
        @apply tw-text-neutral-700;
        .stage-mark {
          @apply tw-border-emerald-600 tw-bg-emerald-600 tw-text-white;
        }
      }
      // Active-state indicator: the stage running now.
      &.is-now {
        @apply tw-font-medium tw-text-neutral-900;
        .stage-mark {
          @apply tw-border-2 tw-border-orange-600;
          border-right-color: transparent;
          animation: read-spin 1s linear infinite;
        }
      }
    }
    .page-chips {
      @apply tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-1.5 tw-p-0;
    }
    .page-chip {
      @apply tw-flex tw-h-8 tw-w-7 tw-items-center tw-justify-center tw-rounded tw-border tw-border-solid tw-border-neutral-200 tw-bg-neutral-50 tw-text-xs tw-tabular-nums tw-text-neutral-600;
      &.is-done {
        @apply tw-border-emerald-600 tw-bg-emerald-50 tw-text-emerald-700;
      }
      &.is-now {
        @apply tw-border-orange-600 tw-bg-white tw-text-neutral-900;
      }
      &.is-failed {
        @apply tw-border-red-500 tw-bg-red-50 tw-text-red-700;
      }
    }
    .progress-foot {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3;
    }
    .found {
      @apply tw-m-0 tw-text-sm tw-font-semibold tw-tabular-nums tw-text-neutral-900;
    }
    .cancel-link {
      @apply tw-min-h-11 tw-shrink-0 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-px-1 tw-text-sm tw-text-neutral-500 tw-underline tw-underline-offset-4 hover:tw-text-neutral-700;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .update-note {
      @apply tw-m-0 tw-text-[13px] tw-text-neutral-500;
    }
    .announcer {
      @apply tw-sr-only;
    }
    .slow-line {
      @apply tw-m-0 tw-text-sm tw-text-amber-700;
    }
    .leave-line {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
  }

  @keyframes read-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-menu-read-progress .stage.is-now .stage-mark {
      animation: none;
    }
  }
</style>
